import React from 'react';
import {
  FDIcon,
  FDStack,
} from '@facet-wealth/design';
import fr from 'facet-rest';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useSurveyStepContext } from 'Views/Onboarding/Pages/Survey/SurveyStep/SurveyStepContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useUserContext from 'Context/UserContext';
import SurveyFixedFooterActions from 'Views/Onboarding/Pages/Survey/SurveyFixedFooterActions';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import MeetingPreparationSection
  from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/CompleteMemberConciergeMeetingConfirmation/MeetingPreparationSection';
import MeetingPreparationSectionMetaOverrideSet
  from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/CompleteMemberConciergeMeetingConfirmation/MeetingPreparationSectionMetaOverrideSet';
import RoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/RoundedButton';
import { DS_SIZE_MAP } from 'Components/ComponentLibrary/DesignSystem/sizes';
import { useChiliPiperContext } from 'Components/ComponentLibrary/Molecules/ChiliPiperComponent/ChiliPiperContext';
import LinkButton from 'Components/ComponentLibrary/Atoms/Inputs/LinkButton';
import useMobileLayout from 'Hooks/useMobileLayout';
import ChiliPiperComponentInFullScreenDialog
  from 'Components/ComponentLibrary/Molecules/ChiliPiperComponent/ChiliPiperComponentInFullScreenDialog';
import history from 'State/history';
import { dashboardUri } from 'Consts/clientUris';

function CompleteMemberConciergeMeetingConfirmationLayout() {
  const {
    resource,
  } = useMetaLayoutContext();
  const { logout } = useUserContext();
  const isMobile = useMobileLayout();
  const [selfServiceOnboardingLoading, setSelfServiceOnboardingLoading] = React.useState(false);

  const { displayErrorMessage } = useSnackbarContext();

  const {
    setOnboardingStepCollection,
  } = useSurveyStepContext();

  const onSelfServiceOnboardingClick = async () => {
    setSelfServiceOnboardingLoading(true);
    try {
      const newStep = await resource.relationship(fr.relationships.selfServiceOnboarding.name).link('related').get();
      setOnboardingStepCollection(newStep);
    } catch (e) {
      if (e.errors) {
        displayErrorMessage(e, e.errors);
      } else {
        displayErrorMessage(e, 'Failed to click, please try again');
      }
    } finally {
      setSelfServiceOnboardingLoading(false);
    }
  };

  const doRedirect = async () => {
    try {
      const newStep = await resource.relationship(fr.relationships.redirectOnClose.name).link('related').get();
      setOnboardingStepCollection(newStep);
    } catch (e) {
      if (e.errors) {
        displayErrorMessage(e, e.errors);
      } else {
        displayErrorMessage(e, 'Failed to redirect to next onboarding step');
      }
    }
  };

  const {
    setOpenIFrame, chiliPiperRel, openIFrame, handleChiliPiperExit,
  } = useChiliPiperContext(resource, doRedirect);


  return (
    ((openIFrame && chiliPiperRel) ? (
      <ChiliPiperComponentInFullScreenDialog chiliPiperRel={chiliPiperRel} handleChiliPiperExit={handleChiliPiperExit} />
    ) : (
      <FDStack spacing={4} direction={'column'}>
        <FDStack spacing={6} direction={'column'} alignSelf={'center'} alignItems={'center'}>
          <FDIcon icon={'fac'} size={'lg'} />
          <MetaControlField attr={'title'} />
          <FDStack direction={'column'} spacing={2} alignItems={'center'}>
            <MetaControlField attr={'meetingDateAndTimeInformation'} />
            {resource.withRelationship(fr.relationships.scheduleChiliPiperMeeting.name, (rel) => (
              <LinkButton
                variant={'h6'}
                onClick={() => {
                  setOpenIFrame(true);
                }}
                test="reschedule-meeting-button"
              >
                {rel.meta('prompt')}
              </LinkButton>
            ),
            () => null)}
          </FDStack>
          <FDStack spacing={2} direction={'column'}>
            {resource.attr('meetingPreparationSections').resources().list().map((meetingPreparationSectionResource) => {
              return (<MetaResourceRenderer
                key={meetingPreparationSectionResource.id()}
                layout={MeetingPreparationSection}
                resource={meetingPreparationSectionResource}
                metaOverrideSet={MeetingPreparationSectionMetaOverrideSet}
              />);
            })
          }
          </FDStack>
        </FDStack>
        <SurveyFixedFooterActions>
          <FDStack spacing={1} direction={'row'} justifyContent={'center'} wrap={isMobile}>
            {resource.withRelationship(fr.relationships.selfServiceOnboarding.name, (rel) => (
              <RoundedButton
                onClick={onSelfServiceOnboardingClick}
                disableElevation
                variant="outlined"
                isLoading={selfServiceOnboardingLoading}
                color={'primary'}
                fullWidth
                size={DS_SIZE_MAP.sm}
                test={'self-service-onboarding-button'}
                useBlueButtonLoader
              >
                {rel.meta('prompt')}
              </RoundedButton>
            ),
            () => null)}
            {resource.withRelationship(fr.relationships.dashboard.name, (rel) => (
              <RoundedButton
                onClick={() => history.push(dashboardUri)}
                disableElevation
                variant="contained"
                fullWidth
                size={DS_SIZE_MAP.sm}
                test={'dashboard-button'}
              >
                {rel.meta('prompt')}
              </RoundedButton>
            ),
            () => null)}
            {resource.withRelationship(fr.relationships.logout.name, (rel) => (
              <RoundedButton
                onClick={logout}
                disableElevation
                variant="contained"
                disabled={selfServiceOnboardingLoading}
                fullWidth
                size={DS_SIZE_MAP.sm}
                test={'log-out-button'}
              >
                {rel.meta('prompt')}
              </RoundedButton>
            ),
            () => null)}
          </FDStack>
        </SurveyFixedFooterActions>
      </FDStack>
    )
    )
  );
}

CompleteMemberConciergeMeetingConfirmationLayout.propTypes = {
};

CompleteMemberConciergeMeetingConfirmationLayout.defaultProps = {
};

export default CompleteMemberConciergeMeetingConfirmationLayout;
